/**
 * OnlineCoffeePassRedemption - Coffee Pass redemption component for Online Ordering
 * Shows when an authenticated user has an active Coffee Pass and is within the redemption window (7am-10am Fiji)
 * Supports size upgrades (8oz FREE, 12oz +$3) and milk upgrades (Regular FREE, specialty +$1)
 */
import { useState, useEffect } from 'react';
import { Coffee, Gift, Clock, Check, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/utils/apiClient';

export default function OnlineCoffeePassRedemption({ userEmail, userPhone, onAddToCart }) {
  const [coffeePassStatus, setCoffeePassStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eligibleItems, setEligibleItems] = useState([]);
  const [milkOptions, setMilkOptions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedMilk, setSelectedMilk] = useState('Regular Milk');
  const [upgradeTo12oz, setUpgradeTo12oz] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [showRedemption, setShowRedemption] = useState(false);
  const [milkUpgradePrice, setMilkUpgradePrice] = useState(1.00);
  const [sizeUpgradePrice, setSizeUpgradePrice] = useState(3.00);

  // Check if user has an active Coffee Pass
  useEffect(() => {
    const checkCoffeePass = async () => {
      if (!userEmail && !userPhone) {
        setLoading(false);
        return;
      }

      try {
        const params = new URLSearchParams();
        if (userEmail) params.append('email', userEmail);
        if (userPhone) params.append('phone', userPhone);
        
        const res = await api.get(`/api/coffee-pass/check?${params.toString()}`);
        setCoffeePassStatus(res.data);
        
        // If can redeem, fetch eligible items
        if (res.data?.can_redeem_now) {
          const itemsRes = await api.get('/api/coffee-pass/eligible-items');
          setEligibleItems(itemsRes.data.items || []);
          setMilkOptions(itemsRes.data.milk_options || [
            { name: 'Regular Milk', price: 0, is_default: true },
            { name: 'Oat Milk', price: 1, is_default: false },
            { name: 'Almond Milk', price: 1, is_default: false },
            { name: 'Soy Milk', price: 1, is_default: false },
            { name: 'Coconut Milk', price: 1, is_default: false }
          ]);
          if (itemsRes.data.milk_upgrade_price) setMilkUpgradePrice(itemsRes.data.milk_upgrade_price);
          if (itemsRes.data.upgrade_price) setSizeUpgradePrice(itemsRes.data.upgrade_price);
        }
      } catch (err) {
        console.error('Failed to check Coffee Pass status:', err);
      } finally {
        setLoading(false);
      }
    };

    checkCoffeePass();
  }, [userEmail, userPhone]);

  // Calculate total price
  const calculatePrice = () => {
    let total = 0;
    if (upgradeTo12oz) total += sizeUpgradePrice;
    if (selectedMilk && selectedMilk !== 'Regular Milk') total += milkUpgradePrice;
    return total;
  };

  // Handle redemption
  const handleRedeem = async () => {
    if (!selectedItem || !coffeePassStatus?.pass_id) return;

    setRedeeming(true);
    try {
      // Build modifiers array
      const modifiers = [];
      if (selectedMilk && selectedMilk !== 'Regular Milk') {
        modifiers.push({
          modifier_name: 'Milk Type',
          selected_option: selectedMilk,
          price_adjustment: milkUpgradePrice
        });
      }

      const res = await api.post('/api/coffee-pass/redeem', {
        pass_id: coffeePassStatus.pass_id,
        item_id: selectedItem.id,
        item_name: selectedItem.name,
        item_category: selectedItem.category || 'coffee',
        upgrade_to_12oz: upgradeTo12oz,
        milk_type: selectedMilk,
        modifiers: modifiers.length > 0 ? modifiers : null,
        channel: 'online'
      });

      if (res.data.success) {
        const finalPrice = calculatePrice();
        
        // Add to cart
        const cartItem = {
          id: selectedItem.id,
          item_id: `coffee-pass-${Date.now()}`,
          name: `${selectedItem.name} (Bula Pass)`,
          price: finalPrice,
          quantity: 1,
          category: selectedItem.category || 'coffee',
          image: selectedItem.image,
          isCoffeeClubRedemption: true,
          isCoffeePassRedemption: true,
          passRedemptionId: res.data.redemption_id,
          size: upgradeTo12oz ? { name: '12oz (Upgraded)', price: sizeUpgradePrice } : { name: '8oz (Free)', price: 0 },
          modifiers: modifiers,
          skip_customization: true
        };

        onAddToCart(cartItem);

        // Build success message
        let description = upgradeTo12oz ? `12oz upgrade +$${sizeUpgradePrice}` : 'Free 8oz';
        if (selectedMilk !== 'Regular Milk') {
          description += ` with ${selectedMilk} +$${milkUpgradePrice}`;
        }

        toast.success(res.data.message || 'Free coffee added to cart!', {
          description,
          icon: <Coffee className="w-4 h-4" />
        });

        // Update status to show redeemed
        setCoffeePassStatus(prev => ({
          ...prev,
          can_redeem_now: false,
          already_redeemed_today: true
        }));
        setShowRedemption(false);
        setSelectedItem(null);
        setSelectedMilk('Regular Milk');
        setUpgradeTo12oz(false);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to redeem Coffee Pass';
      toast.error(errorMsg);
    } finally {
      setRedeeming(false);
    }
  };

  // Don't show if loading or no active pass
  if (loading) return null;
  if (!coffeePassStatus?.has_active_pass) return null;

  const canRedeem = coffeePassStatus?.can_redeem_now;
  const alreadyRedeemed = coffeePassStatus?.already_redeemed_today;
  const windowOpen = coffeePassStatus?.redemption_window_open;
  const hasAfternoonWindow = coffeePassStatus?.has_afternoon_window;
  const planType = coffeePassStatus?.plan_type;
  const planDisplayName = coffeePassStatus?.plan_display_name;
  const redemptionWindows = coffeePassStatus?.redemption_windows || '7:00 AM - 10:00 AM';

  // Already redeemed today
  if (alreadyRedeemed) {
    return (
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl p-4 border border-emerald-200 dark:border-emerald-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
            <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <p className="font-semibold text-emerald-800 dark:text-emerald-200">Coffee Pass Redeemed Today!</p>
            <p className="text-sm text-emerald-600 dark:text-emerald-400">Come back tomorrow for another free coffee</p>
            {hasAfternoonWindow && (
              <p className="text-xs text-emerald-500 dark:text-emerald-500 mt-1">
                Your {planDisplayName} pass: {redemptionWindows}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Outside redemption window
  if (!windowOpen) {
    return (
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl p-4 border border-amber-200 dark:border-amber-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
            <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-semibold text-amber-800 dark:text-amber-200">Bula Coffee Pass Active</p>
              {hasAfternoonWindow && (
                <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-full font-medium">
                  {planType === 'extended' ? 'Extended' : 'Upgraded'}
                </span>
              )}
            </div>
            <p className="text-sm text-amber-600 dark:text-amber-400">
              Free coffee available: <span className="font-semibold">{redemptionWindows}</span>
            </p>
            {hasAfternoonWindow && (
              <p className="text-xs text-amber-500 dark:text-amber-500 mt-1">
                You have both morning AND afternoon windows!
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Can redeem - show redemption UI
  return (
    <div className={`rounded-2xl border-2 overflow-hidden ${
      hasAfternoonWindow 
        ? 'bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 dark:from-purple-900/30 dark:via-indigo-900/30 dark:to-purple-900/30 border-purple-300 dark:border-purple-700'
        : 'bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 dark:from-amber-900/30 dark:via-orange-900/30 dark:to-yellow-900/30 border-amber-300 dark:border-amber-700'
    }`}>
      {/* Header */}
      <div className={`p-4 text-white ${
        hasAfternoonWindow
          ? 'bg-gradient-to-r from-purple-500 to-indigo-500'
          : 'bg-gradient-to-r from-amber-500 to-orange-500'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Gift className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-bold text-lg">Bula Coffee Pass</p>
                {hasAfternoonWindow && (
                  <span className="text-xs px-2 py-0.5 bg-white/20 rounded-full">
                    {planType === 'extended' ? 'Extended' : 'Upgraded'}
                  </span>
                )}
              </div>
              <p className={`text-sm ${hasAfternoonWindow ? 'text-purple-100' : 'text-amber-100'}`}>
                Free coffee ready to redeem!
              </p>
            </div>
          </div>
          <Sparkles className={`w-8 h-8 animate-pulse ${hasAfternoonWindow ? 'text-indigo-200' : 'text-yellow-300'}`} />
        </div>
        {hasAfternoonWindow && (
          <div className="mt-2 pt-2 border-t border-white/20 text-sm text-purple-100">
            Your windows: <span className="font-semibold text-white">{redemptionWindows}</span>
          </div>
        )}
      </div>

      {/* Redemption Content */}
      <div className="p-4">
        {!showRedemption ? (
          <Button
            onClick={() => setShowRedemption(true)}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-6 text-lg rounded-xl"
            data-testid="online-redeem-coffee-pass-btn"
          >
            <Coffee className="w-5 h-5 mr-2" />
            Redeem Your Free Coffee
          </Button>
        ) : (
          <div className="space-y-4">
            {/* Step 1: Select Drink */}
            {!selectedItem ? (
              <div>
                <p className="font-semibold text-amber-800 dark:text-amber-200 mb-3">Select Your Free Coffee:</p>
                <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                  {eligibleItems.slice(0, 12).map(item => (
                    <button
                      key={item.id}
                      onClick={() => setSelectedItem(item)}
                      className="p-3 rounded-xl border-2 border-amber-200 dark:border-amber-700 hover:border-amber-500 hover:bg-amber-100/50 dark:hover:bg-amber-900/30 transition-all text-left"
                      data-testid={`online-coffee-item-${item.id}`}
                    >
                      <p className="font-medium text-gray-900 dark:text-white text-sm truncate">{item.name}</p>
                      <p className="text-xs text-green-600 dark:text-green-400 font-semibold">FREE</p>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Selected Drink */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-amber-200 dark:border-amber-700">
                  <p className="font-semibold text-amber-800 dark:text-amber-200">{selectedItem.name}</p>
                  <button 
                    onClick={() => setSelectedItem(null)}
                    className="text-xs text-amber-600 hover:underline"
                  >
                    Change drink
                  </button>
                </div>

                {/* Size Selection */}
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Size:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setUpgradeTo12oz(false)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        !upgradeTo12oz 
                          ? 'border-amber-500 bg-amber-100 dark:bg-amber-900/40' 
                          : 'border-gray-200 dark:border-gray-700 hover:border-amber-300'
                      }`}
                      data-testid="online-size-8oz"
                    >
                      <p className="font-medium">8oz</p>
                      <p className="text-sm text-green-600 font-bold">FREE</p>
                    </button>
                    <button
                      onClick={() => setUpgradeTo12oz(true)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        upgradeTo12oz 
                          ? 'border-amber-500 bg-amber-100 dark:bg-amber-900/40' 
                          : 'border-gray-200 dark:border-gray-700 hover:border-amber-300'
                      }`}
                      data-testid="online-size-12oz"
                    >
                      <p className="font-medium">12oz</p>
                      <p className="text-sm text-amber-600 font-bold">+${sizeUpgradePrice}</p>
                    </button>
                  </div>
                </div>

                {/* Milk Selection */}
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Milk: <span className="text-amber-600">(Regular FREE, specialty +${milkUpgradePrice})</span>
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {milkOptions.map(milk => (
                      <button
                        key={milk.name}
                        onClick={() => setSelectedMilk(milk.name)}
                        className={`p-2 rounded-lg border-2 transition-all text-center ${
                          selectedMilk === milk.name 
                            ? 'border-amber-500 bg-amber-100 dark:bg-amber-900/40' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-amber-300'
                        }`}
                        data-testid={`online-milk-${milk.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <p className="text-xs font-medium">{milk.name.replace(' Milk', '')}</p>
                        <p className={`text-xs font-bold ${milk.is_default ? 'text-green-600' : 'text-amber-600'}`}>
                          {milk.is_default ? 'FREE' : `+$${milk.price}`}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Total */}
                <div className="bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/40 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Total:</span>
                    <span className={`text-2xl font-bold ${calculatePrice() === 0 ? 'text-green-600' : 'text-amber-600'}`}>
                      {calculatePrice() === 0 ? 'FREE' : `$${calculatePrice().toFixed(2)}`}
                    </span>
                  </div>
                </div>

                {/* Redeem Button */}
                <Button
                  onClick={handleRedeem}
                  disabled={redeeming}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-4 text-lg rounded-xl"
                  data-testid="online-confirm-redeem-btn"
                >
                  {redeeming ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Coffee className="w-5 h-5 mr-2" />
                      Add to Cart {calculatePrice() > 0 ? `($${calculatePrice().toFixed(2)})` : '(Free)'}
                    </>
                  )}
                </Button>

                <Button
                  onClick={() => { setShowRedemption(false); setSelectedItem(null); }}
                  variant="ghost"
                  className="w-full text-gray-500"
                  disabled={redeeming}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
